import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import {
  createVendor,
  deleteVendor,
  getVendorLocations,
  IVendor,
  updateVendor,
  VendorPayload,
} from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'
import { MessageBox } from '@/home/components/messages/MessageBox'

export interface CreateVendorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendor?: IVendor
  editMode: boolean
  editionId: string
  onSuccess: () => void
}

interface IVendorValues {
  name: string
  locations: { value: string }[]
}

const schema = yup.object().shape({
  name: yup.string().required('Field is required'),
  locations: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required('Location name is required'),
      }),
    )
    .min(1, 'Add at least one location')
    .required('Field is required'),
})

export const CreateVendorModal = ({
  open,
  onOpenChange,
  vendor,
  editMode,
  onSuccess,
}: CreateVendorModalProps) => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    reset,

    setValue,
  } = useForm<IVendorValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      locations: [{ value: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locations',
  })

  const getLocations = async () => {
    if (!vendor?.id) return
    try {
      const { data } = await getVendorLocations(vendor?.id)
      setValue(
        'locations',
        data.map((l) => ({ value: l.name })),
      )
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      }
    }
  }

  useEffect(() => {
    if (!open) return
    if (vendor) {
      setValue('name', vendor.name, { shouldValidate: true })
      getLocations()
    }
    return () => {
      reset({ name: '', locations: [{ value: '' }] })
    }
  }, [vendor, open])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: IVendorValues) => {
    const data: VendorPayload = {
      name: values.name,
      locations: values.locations.map((l) => l.value),
    }

    try {
      if (vendor?.id) {
        const { status } = await updateVendor(vendor.id, data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createVendor(data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
      onSuccess()
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteVendorFromModal = async () => {
    try {
      if (vendor) {
        const { status } = await deleteVendor([vendor.id])
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
        onSuccess()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="Vendor Entity"
              title={
                vendor ? (
                  <span className="flex items-center gap-2">
                    Edit <span className="text-accent-1">{vendor.name}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Add new <span className="text-accent-1">Vendor Entity</span>
                  </span>
                )
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={Object.keys(dirtyFields).length === 0}
                form="vendor-form"
                type="submit"
                variant="primary"
              >
                {vendor?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="vendor-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <div className="text-2xl font-bold leading-none text-monochrome-1">
            Locations
          </div>

          <div className="flex flex-col gap-3">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2 relative">
                <Input
                  isInvalid={!!errors?.locations?.[index]?.value?.message}
                  {...register(`locations.${index}.value`)}
                  type="text"
                  placeholder="Enter Location Name"
                  className="flex-1"
                  disabled={!!vendor?.id}
                />
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    disabled={!!vendor?.id}
                    className="shrink-0 absolute right-2 rounded-full p-1 text-monochrome-2 hover:text-monochrome-1"
                  >
                    <Icon
                      icon="close-circle"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                    />
                  </button>
                )}
              </div>
            ))}

            <div className="flex justify-center">
              <Button
                type="button"
                variant="secondary"
                onClick={() => append({ value: '' })}
                disabled={!!vendor?.id}
              >
                Add New
                <Icon
                  icon="plus-square"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle="Vendor"
        deleteFunc={deleteVendorFromModal}
        deletedItem={vendor?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
